import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";

import { Tabs } from "antd";

import { Page } from "../../components/page";
import { TableNotification } from "../../components/table";
import { availableTripClass, TripClass } from "../../store/common_types";
import { bookingsFetchRequest, flightsFetchRequest } from "../../store/gate_tester/actions";
import { RootState } from "../../store/root_reducer";
import { enumParams } from "../../utils/enum_params";
import { MomentParam } from "../../utils/moment_param";

import { BookingsForm, BookingsFormResult, FlightsForm, FlightsFormResult, GateTesterResults } from "./components";
import styles from "./styles.module.scss";

export const GateTesterPage: React.FC = () => {
  const { t } = useTranslation("gate_tester_page");
  const gateTesterState = useSelector((state: RootState) => state.GateTester);
  const [state, setState] = useQueryParams({
    tab: withDefault(
      enumParams<"flights" | "bookings">(StringParam, ["flights", "bookings"]),
      "flights"
    ),
  });

  const [flightsFormState, setFlightsFormState] = useQueryParams({
    origin: withDefault(StringParam, undefined),
    destination: withDefault(StringParam, undefined),
    departureDate: withDefault(MomentParam, undefined),
    returnDate: withDefault(MomentParam, undefined),
    adultsPassengers: withDefault(NumberParam, 1),
    childrenPassengers: withDefault(NumberParam, 0),
    infantsPassengers: withDefault(NumberParam, 0),
    tripClass: withDefault(enumParams<TripClass>(StringParam, availableTripClass), "Y"),
    locale: withDefault(StringParam, undefined),
    currency: withDefault(StringParam, undefined),
    flightsURL: withDefault(StringParam, undefined),
  });

  const [bookingsFormState, setBookingsFormState] = useQueryParams({
    dateFrom: withDefault(MomentParam, undefined),
    dateTo: withDefault(MomentParam, undefined),
    bookingsURL: withDefault(StringParam, undefined),
  });

  const [commonFormState, setCommonFormState] = useQueryParams({
    partner: withDefault(StringParam, undefined),
    password: withDefault(StringParam, undefined),
  });

  const dispatch = useDispatch();

  const errors = useMemo(() => {
    if (state.tab === "flights") {
      return gateTesterState.flights.errors?.errors;
    }
    return gateTesterState.bookings.errors?.errors;
  }, [gateTesterState.bookings, gateTesterState.flights, state.tab]);

  const isLoading = useMemo(() => {
    if (state.tab === "flights") {
      return gateTesterState.flights.isLoading;
    }
    return gateTesterState.bookings.isLoading;
  }, [gateTesterState.bookings, gateTesterState.flights, state.tab]);

  const testerResult = useMemo(() => {
    if (state.tab === "flights") {
      return gateTesterState.flights.result;
    }
    return gateTesterState.bookings.result;
  }, [gateTesterState.bookings, gateTesterState.flights, state.tab]);

  const handleFlightsFormFinish = (value: FlightsFormResult) => {
    const { url, partner, password, ...result } = value;
    setFlightsFormState({ ...result, flightsURL: url });
    setCommonFormState({ partner, password });
    dispatch(flightsFetchRequest(value));
  };

  const handleBookingsFormFinish = (value: BookingsFormResult) => {
    const { url, partner, password, ...result } = value;
    setBookingsFormState({ ...result, bookingsURL: url });
    setCommonFormState({ partner, password });
    dispatch(bookingsFetchRequest(value));
  };

  const handleChangeTab = (tab: string) => {
    setState((c) => ({ ...c, tab: tab as "flights" | "bookings" }));
  };

  return (
    <Page title={t("page.title")} description={t("page.description")}>
      <TableNotification text={t("page.disclaimer")} />
      <div className={styles.page}>
        <Tabs defaultActiveKey={state.tab} onChange={handleChangeTab}>
          <Tabs.TabPane tab={t("page.tabs.flights")} key="flights">
            <FlightsForm
              className={styles.form}
              url={flightsFormState.flightsURL}
              {...flightsFormState}
              {...commonFormState}
              onFinish={handleFlightsFormFinish}
              errors={errors}
              isLoading={isLoading}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab={t("page.tabs.bookings")} key="bookings">
            <BookingsForm
              className={styles.form}
              url={bookingsFormState.bookingsURL}
              {...bookingsFormState}
              {...commonFormState}
              onFinish={handleBookingsFormFinish}
              errors={errors}
              isLoading={isLoading}
            />
          </Tabs.TabPane>
        </Tabs>
        <Tabs style={{ width: "380px", marginLeft: "40px" }}>
          <Tabs.TabPane tab={t("page.tabs.results")}>
            <GateTesterResults tab={state.tab} result={testerResult} />
          </Tabs.TabPane>
        </Tabs>
      </div>
    </Page>
  );
};
